/** @shell standard @header overlay */
import React, { useState, useMemo, useCallback, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  StatusBar,
  Platform,
  Image,
  type TextStyle,
} from 'react-native';
import { SafeAreaView, useSafeAreaInsets } from 'react-native-safe-area-context';
import { useRoute, RouteProp } from '@react-navigation/native';
import type { RootStackParamList } from '../../../navigation/types';
import { SvgIcon } from '../../../components/common/SvgIcon';
import { HeaderBackButton, HEADER_BACK_ICON_SIZE_INLINE } from '../../../components/common/HeaderBackButton';
import { Clickable2 } from '../../../components/common/Clickable2';
import { appTheme, useThemeColors, type ThemeColors } from '../../../shared/theme';
import {
  createStackedFooterPrimaryLabelStyle,
  createStackedFooterPrimarySurfaceStyle,
} from '../../../shared/theme/sharedStyles';
import { useMenuState } from '../../../data/domains/foodMenu';
import { useNutritionUiEnabled } from '../../../data/domains/config';
import { useTranslation, getApiName, getApiDescription, formatNumber, formatDisplayDecimal } from '../../../shared/i18n';
import { horizontalLtrDirectionStyle, useRtl, type UseRtlResult } from '../../../shared/i18n/rtl';
import { useAuthNavigation } from '../../../shared/navigation/useAuthNavigation';
import { useIosStatusBarStyle } from '../../../shared/navigation/useIosStatusBarStyle';
import { DASH_STUDIO_RADIUS } from '../../../shared/theme/studio/stackScreenShell';
import { DASH_STUDIO_INNER } from '../../../shared/theme/studio/dashboardConstants';

type FoodItemDetailRouteProp = RouteProp<RootStackParamList, 'FoodItemDetail'>;

const ANDROID_TEXT_COMPACT: TextStyle = Platform.OS === 'android' ? { includeFontPadding: false } : {};

function normalizeItem(params: FoodItemDetailRouteProp['params'], name_fallback?: string) {
  const item = params?.item;
  if (!item || typeof item !== 'object') {
    return null;
  }
  const name = getApiName(item as Record<string, unknown>) || name_fallback || 'Food Item';
  const a_name = (item as { a_name?: string }).a_name ?? '';
  const description = typeof item.description === 'string' ? item.description : undefined;
  const a_description = (item as { a_description?: string }).a_description ?? '';
  const raw_image = params?.imageUri ?? item.attachment ?? (item as { image?: string }).image ?? undefined;
  const image_uri = typeof raw_image === 'string' && raw_image.trim() !== '' ? raw_image.trim() : undefined;
  const initial_qty = typeof (item as { quantity?: number }).quantity === 'number' ? (item as { quantity: number }).quantity : 0;
  const menu_builder_id = (item as { menu_builder_id?: number; food_type_id?: number }).menu_builder_id
    ?? (item as { food_type_id?: number }).food_type_id
    ?? 0;
  const num = (v: unknown) => {
    if (v == null) return 0;
    const n = typeof v === 'string' ? parseFloat(v) : Number(v);
    return Number.isFinite(n) ? n : 0;
  };
  return {
    id: (item as { id?: number }).id,
    menu_builder_id,
    name,
    a_name,
    imageUri: image_uri,
    description,
    a_description,
    initialQty: initial_qty,
    food_type_category: (item as { food_type_category?: string }).food_type_category,
    calories: num((item as { calories?: unknown }).calories),
    protein: num((item as { protein?: unknown }).protein),
    carbs: num((item as { carbs?: unknown }).carbs),
    fats: num((item as { fats?: unknown }).fats),
  };
}

function createFoodItemDetailSet2Styles(c: ThemeColors, rtl: UseRtlResult) {
  const text_align_primary = rtl.textAlign;
  const writing_direction = rtl.writingDirection;
  const back_align_self = rtl.isRTL ? ('flex-end' as const) : ('flex-start' as const);
  return StyleSheet.create({
    container: {
      flex: 1,
      backgroundColor: c.surface,
    },
    headerOverlay: {
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      zIndex: 4,
      paddingBottom: appTheme.spacing.sm,
      ...horizontalLtrDirectionStyle,
    },
    backButtonWrap: {
      alignSelf: back_align_self,
    },
    emptyState: {
      flex: 1,
      justifyContent: 'center',
      alignItems: 'center',
      padding: appTheme.spacing.xl,
    },
    emptyStateText: {
      fontSize: 16,
      color: c.textSecondary,
      textAlign: 'center',
      writingDirection: writing_direction,
    },
    body: {
      flex: 1,
      minHeight: 0,
      backgroundColor: c.surface,
    },
    /** Image fills the body; title panel overlays the bottom ~30%. */
    imageContainer: {
      ...StyleSheet.absoluteFillObject,
      backgroundColor: c.surfaceLight ?? c.surface,
      overflow: 'hidden',
    },
    image: {
      width: '100%',
      height: '100%',
    },
    imagePlaceholder: {
      ...StyleSheet.absoluteFillObject,
      justifyContent: 'center',
      alignItems: 'center',
      backgroundColor: c.surfaceLight ?? c.surface,
    },
    /** Bottom ~30% sheet — no bottom radius / elevation (those read as a black band). */
    detailsContainer: {
      position: 'absolute',
      left: 0,
      right: 0,
      bottom: 0,
      height: '30%',
      zIndex: 2,
      paddingHorizontal: DASH_STUDIO_INNER,
      paddingTop: appTheme.spacing.lg,
      backgroundColor: c.surface,
      borderTopStartRadius: DASH_STUDIO_RADIUS + 4,
      borderTopEndRadius: DASH_STUDIO_RADIUS + 4,
      overflow: 'hidden',
    },
    detailsScroll: {
      flexGrow: 1,
      paddingBottom: appTheme.spacing.sm,
    },
    titleSection: {
      flexDirection: 'row',
      alignItems: 'flex-start',
      justifyContent: 'space-between',
      gap: appTheme.spacing.sm,
      width: '100%',
      marginBottom: 0,
    },
    itemName: {
      flex: 1,
      minWidth: 0,
      fontSize: 24,
      fontWeight: '700',
      color: c.text,
      letterSpacing: -0.4,
      lineHeight: 30,
      textAlign: text_align_primary,
      writingDirection: writing_direction,
    },
    categoryBadge: {
      backgroundColor: c.primary + '14',
      paddingHorizontal: 10,
      paddingVertical: 5,
      borderRadius: 999,
      marginTop: 2,
      flexShrink: 0,
    },
    categoryText: {
      fontSize: 11,
      fontWeight: '700',
      color: c.primary,
      textTransform: 'uppercase',
      letterSpacing: 0.35,
      textAlign: text_align_primary,
      writingDirection: writing_direction,
      ...ANDROID_TEXT_COMPACT,
    },
    description: {
      fontSize: 15,
      color: c.textSecondary,
      lineHeight: 22,
      marginTop: appTheme.spacing.sm,
      marginBottom: 0,
      width: '100%',
      textAlign: text_align_primary,
      writingDirection: writing_direction,
    },
    sectionTitle: {
      fontSize: 16,
      fontWeight: '700',
      color: c.text,
      marginTop: appTheme.spacing.md,
      marginBottom: appTheme.spacing.sm,
      letterSpacing: -0.2,
      textAlign: text_align_primary,
      writingDirection: writing_direction,
    },
    nutritionGrid: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      gap: 8,
      ...horizontalLtrDirectionStyle,
    },
    nutritionCard: {
      flex: 1,
      paddingVertical: 10,
      paddingHorizontal: 6,
      alignItems: 'center',
      backgroundColor: c.surfaceLight ?? c.backgroundElevated ?? c.background,
      borderRadius: 14,
    },
    nutritionLabel: {
      fontSize: 10,
      color: c.textSecondary,
      fontWeight: '600',
      marginBottom: 4,
      textAlign: 'center',
      ...ANDROID_TEXT_COMPACT,
    },
    nutritionValue: {
      fontSize: 14,
      fontWeight: '700',
      color: c.text,
      textAlign: 'center',
      writingDirection: 'ltr',
      ...ANDROID_TEXT_COMPACT,
    },
    nutritionUnit: {
      fontSize: 10,
      color: c.textSecondary,
      fontWeight: '500',
    },
    footer: {
      backgroundColor: c.surface,
      borderTopWidth: StyleSheet.hairlineWidth,
      borderTopColor: c.borderLight,
    },
    footerContent: {
      paddingHorizontal: appTheme.spacing.lg,
      paddingTop: appTheme.spacing.sm,
      paddingBottom: appTheme.spacing.xs,
      gap: appTheme.spacing.sm,
      width: '100%',
    },
    quantitySection: {
      gap: 6,
      alignItems: 'stretch',
      width: '100%',
    },
    quantityLabel: {
      fontSize: 12,
      fontWeight: '600',
      color: c.textSecondary,
      textAlign: text_align_primary,
      writingDirection: writing_direction,
      ...ANDROID_TEXT_COMPACT,
    },
    quantityRow: {
      flexDirection: 'row',
      alignItems: 'center',
      alignSelf: 'flex-start',
      backgroundColor: c.background,
      borderRadius: 999,
      borderWidth: StyleSheet.hairlineWidth,
      borderColor: c.borderLight,
      paddingHorizontal: 4,
      paddingVertical: 4,
      gap: 4,
      ...horizontalLtrDirectionStyle,
    },
    quantityButton: {
      width: 40,
      height: 40,
      borderRadius: 20,
      backgroundColor: c.primary,
      alignItems: 'center',
      justifyContent: 'center',
    },
    quantityButtonDisabled: {
      backgroundColor: c.borderLight,
      opacity: 0.55,
    },
    quantityButtonLabel: {
      fontSize: 20,
      fontWeight: '700',
      lineHeight: 22,
      color: c.onPrimary,
      textAlign: 'center',
      ...ANDROID_TEXT_COMPACT,
    },
    quantityText: {
      fontSize: 18,
      fontWeight: '800',
      color: c.text,
      minWidth: 36,
      textAlign: 'center',
      writingDirection: 'ltr',
      ...ANDROID_TEXT_COMPACT,
    },
    primaryCta: {
      alignSelf: 'stretch',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: 52,
      width: '100%',
    },
    primaryCtaDisabled: {
      opacity: 0.45,
    },
    primaryCtaLabel: {
      flexShrink: 1,
      width: '100%',
      textAlign: 'center',
      writingDirection: writing_direction,
    },
  });
}

export function FoodItemDetailScreenSet2() {
  useIosStatusBarStyle('light');
  const { t } = useTranslation();
  const navigation = useAuthNavigation();
  const insets = useSafeAreaInsets();
  const route = useRoute<FoodItemDetailRouteProp>();
  const name_fallback = t('foodItemDetail.foodItemFallback');
  const normalized = useMemo(() => normalizeItem(route.params, name_fallback), [route.params, name_fallback]);
  const { updateItemQuantity, selectedDate, getSlot } = useMenuState();
  const nutrition_ui_enabled = useNutritionUiEnabled();
  const c = useThemeColors() ?? appTheme.colors;
  const rtl = useRtl();
  const styles = useMemo(() => createFoodItemDetailSet2Styles(c, rtl), [c, rtl]);
  const primary_footer = useMemo(
    () => ({
      surface: createStackedFooterPrimarySurfaceStyle(c),
      label: createStackedFooterPrimaryLabelStyle(c),
    }),
    [c],
  );

  const [quantity, setQuantity] = useState(normalized?.initialQty ?? 0);

  useEffect(() => {
    setQuantity(normalized?.initialQty ?? 0);
  }, [normalized?.id, normalized?.initialQty]);

  const is_in_current_menu = useMemo(() => {
    if (!normalized?.id || !selectedDate || normalized.menu_builder_id == null) {
      return false;
    }
    const slot = getSlot(selectedDate);
    if (!slot?.menuBuilders) {
      return false;
    }
    const builder = slot.menuBuilders.find(b => b.food_type_id === normalized.menu_builder_id);
    if (!builder?.type_data) {
      return false;
    }
    for (const it of builder.type_data) {
      if (it.id === normalized.id) {
        return true;
      }
      for (const r of (it as { replaceable_menu_items?: Array<{ id?: number }> }).replaceable_menu_items ?? []) {
        if (r.id === normalized.id) {
          return true;
        }
      }
    }
    return false;
  }, [normalized?.id, normalized?.menu_builder_id, selectedDate, getSlot]);

  const nutrition_data = useMemo(() => {
    if (!normalized || !nutrition_ui_enabled) {
      return [];
    }
    const fmt = (v: number) => (Number.isFinite(v) ? formatDisplayDecimal(v, 1) : formatDisplayDecimal(0, 1));
    return [
      { label: t('foodItemDetail.cal'), value: fmt(normalized.calories), unit: 'kcal' },
      { label: t('foodItemDetail.protein'), value: fmt(normalized.protein), unit: 'g' },
      { label: t('foodItemDetail.carbs'), value: fmt(normalized.carbs), unit: 'g' },
      { label: t('foodItemDetail.fats'), value: fmt(normalized.fats), unit: 'g' },
    ];
  }, [normalized, nutrition_ui_enabled, t]);

  const handle_quantity_change = useCallback((increment: boolean) => {
    setQuantity((prev: number) => (increment ? prev + 1 : prev > 0 ? prev - 1 : 0));
  }, []);

  const handle_add_to_menu = useCallback(() => {
    if (!normalized?.id || quantity === 0 || normalized.menu_builder_id == null) {
      return;
    }
    if (!is_in_current_menu) {
      navigation.goBack();
      return;
    }
    const delta = quantity - normalized.initialQty;
    if (delta > 0) {
      for (let i = 0; i < delta; i++) {
        updateItemQuantity(normalized.menu_builder_id, normalized.id, true);
      }
    } else if (delta < 0) {
      for (let i = 0; i < -delta; i++) {
        updateItemQuantity(normalized.menu_builder_id, normalized.id, false);
      }
    }
    navigation.goBack();
  }, [normalized?.id, normalized?.menu_builder_id, normalized?.initialQty, quantity, is_in_current_menu, updateItemQuantity, navigation]);

  const render_back_header = (on_press: () => void) => (
    <View
      style={[
        styles.headerOverlay,
        {
          paddingTop: insets.top + 10,
          paddingLeft: Math.max(appTheme.spacing.md, insets.left),
          paddingRight: Math.max(appTheme.spacing.md, insets.right),
        },
      ]}
      pointerEvents="box-none">
      <View style={styles.backButtonWrap}>
        <HeaderBackButton
          onPress={on_press}
          accessibilityLabel={t('common.back')}
          appearance="elevated"
          iconSize={HEADER_BACK_ICON_SIZE_INLINE}
        />
      </View>
    </View>
  );

  if (!normalized) {
    return (
      <View style={styles.container}>
        {Platform.OS === 'android' ? <StatusBar barStyle="light-content" /> : null}
        {render_back_header(() => navigation.goBack())}
        <View style={styles.emptyState}>
          <Text style={styles.emptyStateText}>{t('foodItemDetail.noItemData')}</Text>
        </View>
      </View>
    );
  }

  const item = normalized;
  const source_item = (route.params?.item ?? item) as Record<string, unknown>;
  const display_name = getApiName(source_item) || item.name;
  const raw_description = getApiDescription(source_item).trim();
  /** Show locale description when present; skip empty or title-duplicate copy. */
  const description_text =
    raw_description && raw_description.toLowerCase() !== display_name.trim().toLowerCase()
      ? raw_description
      : undefined;
  const add_label = t('foodItemDetail.addToMenu', { count: quantity });
  const can_confirm = is_in_current_menu && quantity > 0;
  const show_macros =
    nutrition_ui_enabled && nutrition_data.some(n => Number.parseFloat(String(n.value)) > 0);
  const details_bottom_pad = is_in_current_menu
    ? appTheme.spacing.md
    : Math.max(insets.bottom, appTheme.spacing.md);

  return (
    <View style={styles.container}>
      {Platform.OS === 'android' ? <StatusBar barStyle="light-content" /> : null}
      {render_back_header(() => navigation.goBack())}

      <View style={styles.body}>
        <View style={styles.imageContainer}>
          {item.imageUri ? (
            <Image source={{ uri: item.imageUri }} style={styles.image} resizeMode="cover" />
          ) : (
            <View style={styles.imagePlaceholder}>
              <SvgIcon name="menu" size={72} color={c.borderLight} />
            </View>
          )}
        </View>

        <View style={[styles.detailsContainer, { paddingBottom: details_bottom_pad }]}>
          <ScrollView
            style={{ flex: 1 }}
            contentContainerStyle={styles.detailsScroll}
            showsVerticalScrollIndicator={false}
            bounces={false}>
            <View style={styles.titleSection}>
              <Text style={styles.itemName}>{display_name}</Text>
              {item.food_type_category ? (
                <View style={styles.categoryBadge}>
                  <Text style={styles.categoryText}>
                    {String(item.food_type_category).toLowerCase() === 'meal'
                      ? t('foodItemDetail.meal')
                      : t('foodItemDetail.snack')}
                  </Text>
                </View>
              ) : null}
            </View>

            {description_text ? <Text style={styles.description}>{description_text}</Text> : null}

            {show_macros ? (
              <View>
                <Text style={styles.sectionTitle}>{t('foodItemDetail.nutritionalInformation')}</Text>
                <View style={styles.nutritionGrid}>
                  {nutrition_data.map((nutrient, index) => (
                    <View key={index} style={styles.nutritionCard}>
                      <Text style={styles.nutritionLabel}>{nutrient.label}</Text>
                      <Text style={styles.nutritionValue}>
                        {nutrient.value}
                        <Text style={styles.nutritionUnit}> {nutrient.unit}</Text>
                      </Text>
                    </View>
                  ))}
                </View>
              </View>
            ) : null}
          </ScrollView>
        </View>
      </View>

      {is_in_current_menu ? (
        <SafeAreaView edges={['bottom']} style={styles.footer}>
          <View
            style={[
              styles.footerContent,
              {
                paddingLeft: Math.max(appTheme.spacing.lg, insets.left),
                paddingRight: Math.max(appTheme.spacing.lg, insets.right),
              },
            ]}>
            <View style={styles.quantitySection}>
              <Text style={styles.quantityLabel}>{t('foodItemDetail.quantity')}</Text>
              <View style={styles.quantityRow}>
                <Clickable2
                  onPress={() => handle_quantity_change(false)}
                  disabled={quantity === 0}
                  accessibilityLabel={t('menu.decreaseQuantity')}
                  accessibilityRole="button">
                  <View style={[styles.quantityButton, quantity === 0 && styles.quantityButtonDisabled]}>
                    <Text style={styles.quantityButtonLabel}>−</Text>
                  </View>
                </Clickable2>
                <Text style={styles.quantityText}>{formatNumber(quantity, { useGrouping: false })}</Text>
                <Clickable2
                  onPress={() => handle_quantity_change(true)}
                  accessibilityLabel={t('menu.increaseQuantity')}
                  accessibilityRole="button">
                  <View style={styles.quantityButton}>
                    <Text style={styles.quantityButtonLabel}>+</Text>
                  </View>
                </Clickable2>
              </View>
            </View>
            <Clickable2
              onPress={handle_add_to_menu}
              disabled={!can_confirm}
              accessibilityLabel={add_label}
              accessibilityRole="button"
              style={{ alignSelf: 'stretch' }}>
              <View style={[primary_footer.surface, styles.primaryCta, !can_confirm && styles.primaryCtaDisabled]}>
                <Text style={[primary_footer.label, styles.primaryCtaLabel]}>{add_label}</Text>
              </View>
            </Clickable2>
          </View>
        </SafeAreaView>
      ) : null}
    </View>
  );
}
